import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from '@rocketlane/rocket-router';
import { memo, useCallback, useState } from 'react';
import RocketlaneLogo from 'assets/rocketlane_logo_64px.png';
import { Popover } from 'components/atoms/popover';
import nitroClawLogo from 'components/organisms/nitro-claw/assets/nitro-claw-logo.svg';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useAppShellMode } from 'hooks/use-app-shell-mode';
import { useTranslate } from 'hooks/use-translation';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { CheckIcon, Mark, MarkImage, Menu, OptionButton, OptionLabel, TriggerButton } from './app-shell-switcher.styles';
const OPTIONS = [
    {
        mode: 'rocketlane',
        labelKey: 'rocketlane',
        labelFallback: 'Rocketlane',
        iconSrc: RocketlaneLogo
    },
    {
        mode: 'nitro-claw',
        labelKey: 'desktop.nav.app_name',
        labelFallback: 'Nitro Claw',
        iconSrc: nitroClawLogo
    }
];
/**
 * Logo / brand trigger that opens a Rocketlane ↔ Nitro Claw shell switcher.
 * When `AccountFeature.NitroClaw` is off, renders children only (no popover).
 */ export const AppShellSwitcher = /*#__PURE__*/ memo(({ children, placement = 'rightTop', fill = false })=>{
    const translate = useTranslate();
    const navigate = useNavigate();
    const { mode, setMode } = useAppShellMode();
    const [isNitroClawEnabled] = useAccountFeature(AccountFeature.NitroClaw);
    const [open, setOpen] = useState(false);
    const selectMode = useCallback((next)=>{
        setOpen(false);
        if (next === mode) {
            return;
        }
        setMode(next);
        // Hard cut between shells: land on that shell's home and clear hash
        // so Rocketlane drawers / Nitro routes never carry over.
        navigate({
            pathname: next === 'nitro-claw' ? `/${ROUTE_PATHS.NITRO_CLAW}` : ROUTE_PATHS.ROOT,
            search: '',
            hash: ''
        });
    }, [
        mode,
        navigate,
        setMode
    ]);
    if (!isNitroClawEnabled) {
        return children;
    }
    const content = /*#__PURE__*/ _jsx(Menu, {
        "data-cy": "app-shell-switcher.menu",
        role: "menu",
        children: OPTIONS.map((option)=>{
            const selected = mode === option.mode;
            return /*#__PURE__*/ _jsxs(OptionButton, {
                type: "button",
                role: "menuitemradio",
                "aria-checked": selected,
                $selected: selected,
                "data-cy": `app-shell-switcher.option.${option.mode}`,
                onClick: ()=>selectMode(option.mode),
                children: [
                    /*#__PURE__*/ _jsx(Mark, {
                        "aria-hidden": true,
                        children: /*#__PURE__*/ _jsx(MarkImage, {
                            src: option.iconSrc,
                            alt: ""
                        })
                    }),
                    /*#__PURE__*/ _jsx(OptionLabel, {
                        children: translate(option.labelKey, undefined, option.labelFallback)
                    }),
                    selected ? /*#__PURE__*/ _jsx(CheckIcon, {
                        "aria-hidden": true,
                        children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                            icon: faCheck
                        })
                    }) : null
                ]
            }, option.mode);
        })
    });
    return /*#__PURE__*/ _jsx(Popover, {
        trigger: "click",
        placement: placement,
        open: open,
        onOpenChange: setOpen,
        content: content,
        children: /*#__PURE__*/ _jsx(TriggerButton, {
            type: "button",
            $fill: fill,
            "data-cy": "app-shell-switcher.trigger",
            "aria-label": translate('app_shell_switcher.trigger', undefined, 'Switch app'),
            "aria-haspopup": "menu",
            "aria-expanded": open,
            children: children
        })
    });
});
AppShellSwitcher.displayName = 'AppShellSwitcher';
